import React, { useEffect, useRef, useState } from 'react';
import { RotateCcw, SendHorizontal } from 'lucide-react';
import { powerBiService } from '../../services/api';
import styles from './AcademicsAgentPanel.module.css';

const CHAT_STORAGE_KEY = 'analyticax-analytics-chat';

const SUGGESTIONS = [
  'How many Admissions rows are there?',
  'Summarize Attendance',
  'Show recent Payments',
];

function loadStoredMessages() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(CHAT_STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((message) => message && (message.role === 'user' || message.role === 'assistant') && message.content)
      .slice(-12)
      .map((message) => ({
        id: message.id || messageId(),
        role: message.role,
        content: String(message.content).slice(0, 4000),
        error: Boolean(message.error),
        queryCount: Number(message.queryCount) || 0,
      }));
  } catch (error) {
    return [];
  }
}

let nextId = 1;
const messageId = () => `msg-${nextId++}`;

function renderInline(text, keyPrefix) {
  const pieces = String(text).split(/(\*\*[^*]+\*\*)/g);
  return pieces.map((piece, index) => {
    if (piece.startsWith('**') && piece.endsWith('**') && piece.length > 4) {
      return <strong key={`${keyPrefix}-${index}`}>{piece.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${keyPrefix}-${index}`}>{piece}</React.Fragment>;
  });
}

function isTableRow(line) {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.includes('|');
}

function isDividerRow(line) {
  return /^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|$/.test(line.trim());
}

function splitCells(line) {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

function AnswerBody({ text }) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (line.trim().startsWith('```')) {
      const code = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    if (isTableRow(line) && index + 1 < lines.length && isDividerRow(lines[index + 1])) {
      const headers = splitCells(line);
      const rows = [];
      index += 2;
      while (index < lines.length && isTableRow(lines[index]) && !isDividerRow(lines[index])) {
        rows.push(splitCells(lines[index]));
        index += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*[-*]\s+/, ''));
        index += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    const paragraph = [];
    while (
      index < lines.length
      && lines[index].trim()
      && !lines[index].trim().startsWith('```')
      && !/^\s*[-*]\s+/.test(lines[index])
      && !(isTableRow(lines[index]) && index + 1 < lines.length && isDividerRow(lines[index + 1]))
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
  }

  return (
    <div className={styles.answerBody}>
      {blocks.map((block, blockIndex) => {
        if (block.type === 'code') {
          return <pre key={blockIndex} className={styles.codeBlock}>{block.text}</pre>;
        }
        if (block.type === 'list') {
          return (
            <ul key={blockIndex}>
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item, `${blockIndex}-${itemIndex}`)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === 'table') {
          return (
            <div key={blockIndex} className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    {block.headers.map((header, headerIndex) => (
                      <th key={headerIndex}>{renderInline(header, `h-${blockIndex}-${headerIndex}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {block.headers.map((_, cellIndex) => (
                        <td key={cellIndex}>{renderInline(row[cellIndex] || '', `c-${blockIndex}-${rowIndex}-${cellIndex}`)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return <p key={blockIndex}>{renderInline(block.text, `p-${blockIndex}`)}</p>;
      })}
    </div>
  );
}

const AcademicsAgentPanel = ({ active }) => {
  const [messages, setMessages] = useState(loadStoredMessages);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!active) return undefined;
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [active]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, [messages, active]);

  useEffect(() => {
    const stored = messages
      .filter((message) => !message.pending && message.content)
      .slice(-12);
    sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(stored));
  }, [messages]);

  const ask = async (rawQuestion) => {
    const question = String(rawQuestion || '').trim();
    if (!question || sending) return;

    const history = messages
      .filter((message) => !message.pending && !message.error && (message.role === 'user' || message.role === 'assistant'))
      .slice(-12)
      .map((message) => ({ role: message.role, content: message.content }));

    const userMessage = { id: messageId(), role: 'user', content: question };
    const pendingMessage = { id: messageId(), role: 'assistant', content: '', pending: true };
    setMessages((current) => [...current, userMessage, pendingMessage]);
    setDraft('');
    setSending(true);
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    try {
      const result = await powerBiService.askAnalyticsAgent(question, history);
      if (requestRef.current !== requestId) return;
      setMessages((current) => current.map((message) => (
        message.id === pendingMessage.id
          ? {
            ...message,
            pending: false,
            content: result.answer || 'Sorry, I could not work out an answer to that question.',
            queryCount: result.queryCount || 0,
          }
          : message
      )));
    } catch (error) {
      if (requestRef.current !== requestId) return;
      setMessages((current) => current.map((message) => (
        message.id === pendingMessage.id
          ? {
            ...message,
            pending: false,
            error: true,
            content: error.message || 'Could not answer that question.',
          }
          : message
      )));
    } finally {
      if (requestRef.current === requestId) setSending(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    ask(draft);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      ask(draft);
    }
  };

  const clearChat = () => {
    requestRef.current += 1;
    setSending(false);
    setMessages([]);
    setDraft('');
    sessionStorage.removeItem(CHAT_STORAGE_KEY);
    inputRef.current?.focus();
  };

  return (
    <section className={styles.panel} aria-label="Analytics assistant">
      <header className={styles.header}>
        <div>
          <h3 className={styles.title}>AI Mode</h3>
          <p className={styles.subtitle}>Academics, Attendance, Admissions, Receivables, and Payments for your account.</p>
        </div>
        {messages.length > 0 && (
          <button type="button" className={styles.clearButton} onClick={clearChat} aria-label="Clear conversation">
            <RotateCcw size={16} strokeWidth={2.25} />
          </button>
        )}
      </header>

      <div className={styles.messages} ref={listRef} aria-live="polite">
        {messages.length === 0 && (
          <div className={styles.empty}>
            <p>Ask about your Academics, Attendance, Admissions, Receivables, or Payments data.</p>
            <div className={styles.suggestions}>
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className={styles.suggestion}
                  onClick={() => ask(suggestion)}
                  disabled={sending}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <article
            key={message.id}
            className={`${styles.message} ${message.role === 'user' ? styles.userMessage : styles.assistantMessage} ${message.error ? styles.errorMessage : ''}`}
          >
            {message.pending ? (
              <div className={styles.pending}>
                <span className={styles.dots} aria-hidden="true"><i /><i /><i /></span>
                Checking your analytics tables
              </div>
            ) : message.role === 'assistant' ? (
              <>
                <AnswerBody text={message.content} />
                {!message.error && message.queryCount > 0 && (
                  <p className={styles.queryNote}>
                    Looked up {message.queryCount} {message.queryCount === 1 ? 'query' : 'queries'} in your analytics tables.
                  </p>
                )}
              </>
            ) : (
              <p>{message.content}</p>
            )}
          </article>
        ))}
      </div>

      <form className={styles.composer} onSubmit={onSubmit}>
        <textarea
          ref={inputRef}
          className={styles.input}
          rows={2}
          value={draft}
          placeholder="Ask about Academics, Attendance, Admissions, Receivables, or Payments"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          disabled={sending}
          maxLength={2000}
          aria-label="Question about your analytics tables"
        />
        <button type="submit" className={styles.sendButton} disabled={sending || !draft.trim()} aria-label="Send question">
          <SendHorizontal size={16} />
        </button>
      </form>
    </section>
  );
};

export default AcademicsAgentPanel;
